import type { SessionDataSource } from '../../core/services/session-data-source';
import { ParentAuthService } from '../parent-auth.service';
import { ParentOfflineProfile } from './parent-offline-profile';
import { Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, concat, defer, finalize, forkJoin, map, Observable, of, switchMap, tap, throwError, toArray } from 'rxjs';
import {
  PatientModel,
  ParentBehaviorOptionsModel,
  PatientBehaviorDataModel,
  PatientBehaviorModel,
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  TargetType,
} from '../../core/api/models';
import { ParentApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import '../../shared/utils/utils';

type StoredParentSession = PatientSessionModel & { isDirty?: boolean };

/**
 * Provides the shared collection controls with parent-authorized session data. Like the staff offline sessions, each parent
 * session is kept on the device and sent whole, so trials and Finish survive a lost connection or a closed tab.
 */
@Injectable({
  providedIn: 'root',
})
export class ParentSessionService implements SessionDataSource {
  dataChanged = signal<number>(0);

  private parentAuth = inject(ParentAuthService);
  private get storageKey(): string { return 'parentSessions:' + this.parentAuth.storageScope; }
  private get profileKey(): string { return 'parentProfiles:' + this.parentAuth.storageScope; }
  private get patientsKey(): string { return 'parentPatients:' + this.parentAuth.storageScope; }
  private get runningDataKey(): string { return 'parentTargetsRunningData:' + this.parentAuth.storageScope; }
  private loadedStorageKey = '';
  private readonly quietPeriodMs = 2000;
  private readonly retryDelayMs = 15000;

  private sessions: StoredParentSession[] = [];
  private session: StoredParentSession | null = null;
  private targets: PatientTargetModel[] = [];
  private behaviors: PatientBehaviorModel[] = [];
  private behaviorOptions: ParentBehaviorOptionsModel = {};
  private targetsRunningDataCache: { SessionID: number; TargetID: number; Data: any }[] = [];
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private syncingSessionIDs = new Set<number | undefined>();
  private changeVersion = 0;

  constructor(private parentApiService: ParentApiService, private alertService: AlertService) {
    window.addEventListener('online', () => this.syncPendingSessions().subscribe());
  }

  getPatients(offline = false): Observable<PatientModel[]> {
    const patientsKey = this.patientsKey;
    if (offline) return of(this.getOfflinePatients());
    return this.parentApiService.GetPatients().pipe(
      tap(patients => {
        if (patientsKey !== this.patientsKey) throw new HttpErrorResponse({status: 401});
        localStorage.setItem(patientsKey, JSON.stringify(patients));
      }),
      catchError(error => error.status === 0 ? of(JSON.parse(localStorage.getItem(this.patientsKey) ?? '[]')) : throwError(() => error))
    );
  }

  getOfflinePatients(): PatientModel[] {
    const profiles: { [id: number]: ParentOfflineProfile } = JSON.parse(localStorage.getItem(this.profileKey) ?? '{}');
    const patients: PatientModel[] = JSON.parse(localStorage.getItem(this.patientsKey) ?? '[]');
    return patients.filter(patient => profiles[patient.ID ?? 0]);
  }

  getOfflineProfile(patientID: number): ParentOfflineProfile | undefined {
    const profiles: { [id: number]: ParentOfflineProfile } = JSON.parse(localStorage.getItem(this.profileKey) ?? '{}');
    return this.getOfflinePatients().some(x => x.ID === patientID) ? profiles[patientID] : undefined;
  }

  loadCollectionData(patientID: number): Observable<ParentOfflineProfile> {
    const profileKey = this.profileKey;
    const cached = this.getOfflineProfile(patientID);
    if (!navigator.onLine && cached) return of(cached);
    return forkJoin({
      patients: this.getPatients(),
      targets: this.parentApiService.GetTargets({ patientID }),
      behaviors: this.parentApiService.GetBehaviors({ patientID }),
      options: this.parentApiService.GetBehaviorOptions({ patientID }),
      playlists: this.parentApiService.GetPlaylists({ patientID }),
      programs: this.parentApiService.GetPrograms({ patientID }),
      promptSchedules: this.parentApiService.GetPromptSchedules({ patientID }),
    }).pipe(map(({ patients, ...data }) => {
      if (profileKey !== this.profileKey) throw new HttpErrorResponse({status: 401});
      const patient = patients.find(x => x.ID === patientID);
      if (!patient) throw new Error('This client is not available for parent data collection.');
      const profile = { patient, ...data };
      const profiles = JSON.parse(localStorage.getItem(this.profileKey) ?? '{}');
      profiles[patientID] = profile;
      localStorage.setItem(this.profileKey, JSON.stringify(profiles));
      return profile;
    }), catchError(error => error.status === 0 && cached ? of(cached) : throwError(() => error)));
  }

  getCachedSessions(patientID: number): PatientSessionModel[] {
    this.loadSessions();
    return this.sessions.filter(x => x.PatientID === patientID).sort((a, b) => (b.StartedDate ?? '').localeCompare(a.StartedDate ?? ''));
  }

  rememberSessions(sessions: PatientSessionModel[]): void {
    this.loadSessions();
    // Legacy caches had no account namespace. Adopt only sessions confirmed by this account's API.
    const legacy: StoredParentSession[] = JSON.parse(localStorage.getItem('parentSessions') ?? '[]');
    const confirmedIDs = new Set(sessions.filter(x => legacy.some(old => old.ID === x.ID && old.PatientID === x.PatientID && !!old.StartedDate && old.StartedDate === x.StartedDate)).map(x => x.ID));
    for (const session of sessions) {
      const previous = legacy.find(x => x.ID === session.ID && x.PatientID === session.PatientID && !!x.StartedDate && x.StartedDate === session.StartedDate);
      if (previous?.isDirty && !this.sessions.some(x => x.ID === session.ID && x.isDirty)) this.storeSession(previous);
      const existing = this.sessions.find(x => x.ID === session.ID);
      if (!existing?.isDirty) this.storeSession(session);
    }
    if (confirmedIDs.size) localStorage.setItem('parentSessions', JSON.stringify(legacy.filter(x => !confirmedIDs.has(x.ID))));
  }

  getUnsyncedDataJson(): string {
    this.loadSessions();
    return JSON.stringify(this.sessions.filter(x => x.isDirty));
  }

  leaveSession(): void {
    if (this.syncTimer) clearTimeout(this.syncTimer);
    this.syncTimer = null;
    this.session = null;
    this.targets = [];
    this.behaviors = [];
  }

  /** Opens or resumes a parent's session using the saved client profile when disconnected. */
  startSession(patientID: number, targets: PatientTargetModel[], behaviors: PatientBehaviorModel[] = [], options: ParentBehaviorOptionsModel = {}): Observable<PatientSessionModel> {
    return defer(() => {
      this.loadSessions();
      const storageKey = this.storageKey;
      const offlineSession = () => this.sessions.find(x => x.PatientID === patientID && !x.FinishedDate) ?? {
        ID: -Date.now(), PatientID: patientID, SparkModeID: 3, StartedDate: new Date().toServerString(), TargetData: [], BehaviorData: [], isDirty: true
      };
      const request = navigator.onLine ? this.syncPendingSessions().pipe(
        switchMap(() => this.parentApiService.StartSession({ patientID })),
        catchError(error => error.status === 0 ? of(offlineSession()) : throwError(() => error))
      ) : of(offlineSession());
      return request.pipe(map(serverSession => {
        if (storageKey !== this.storageKey) throw new HttpErrorResponse({status: 401});
        if (navigator.onLine && (serverSession.ID ?? 0) > 0) this.rememberSessions([serverSession]);
        const storedSession = this.sessions.find(x => x.ID === serverSession.ID);
        this.session = storedSession?.isDirty ? storedSession : { ...serverSession, TargetData: serverSession.TargetData ?? [] };
        this.targets = targets;
        this.behaviors = behaviors;
        this.behaviorOptions = options;
        this.session.BehaviorData ??= [];
        this.storeSession(this.session);
        this.dataChanged.update(n => n + 1);
        return this.session;
      }));
    });
  }

  /** Marks the session finished on the device and tries to send it; resolves to false when it is still waiting to be sent. */
  finishSession(session?: PatientSessionModel): Observable<boolean> {
    this.loadSessions();
    if (session) this.session = this.sessions.find(x => x.ID === session.ID) ?? session;
    if (!this.session) {
      return of(false);
    }

    this.session.FinishedDate = new Date().toServerString();
    this.markDirty();
    return navigator.onLine ? this.syncSession(this.session, true) : of(false);
  }

  syncPendingSessions(): Observable<boolean> {
    this.loadSessions();
    if (!navigator.onLine || !this.parentAuth.isSignedIn()) return of(false);
    const pendingSessions = this.sessions.filter((s) => s.isDirty);
    if (pendingSessions.length === 0) {
      return of(true);
    }

    return concat(...pendingSessions.map((s) => this.syncSession(s, s === this.session))).pipe(
      toArray(),
      map((results) => results.every((synced) => synced))
    );
  }

  hasRunningTimers(sessionID: number | undefined = this.session?.ID): boolean {
    this.loadTargetsRunningData();
    return this.targetsRunningDataCache.some(x => x.SessionID === sessionID && x.Data?.isStarted);
  }

  getPatientSession(): PatientSessionModel | null {
    this.loadSessions();
    return this.session;
  }

  getBehaviorData(behaviorID: number): PatientBehaviorDataModel[] {
    return this.session?.BehaviorData?.filter(x => x.PatientBehaviorID === behaviorID) ?? [];
  }

  getPatientBehaviors(): PatientBehaviorModel[] { return this.behaviors; }
  getPatientBehaviorLocations() { return this.behaviorOptions.Locations ?? []; }
  getPatientBehaviorResponses() { return this.behaviorOptions.Responses ?? []; }
  getPatientBehaviorSeverities() { return this.behaviorOptions.Severities ?? []; }
  getPatientBehaviorConsequences() { return [...(this.behaviorOptions.Consequences ?? [])]; }
  getPatientBehaviorAntecedents() { return [...(this.behaviorOptions.Antecedents ?? [])]; }

  addBehaviorData(data: PatientBehaviorDataModel): void { this.saveBehaviorData(data); }

  saveBehaviorData(data: PatientBehaviorDataModel, originalRecordedDate?: string): void {
    if (!this.session?.BehaviorData) return;
    data.PatientSessionID = this.session.ID;
    data.RecordedDate ??= new Date().toServerString();
    const index = this.session.BehaviorData.findIndex(x => x.PatientBehaviorID === data.PatientBehaviorID &&
      ((!!data.ID && x.ID === data.ID) || x.RecordedDate === (originalRecordedDate ?? data.RecordedDate)));
    if (index < 0) this.session.BehaviorData.push(data);
    else this.session.BehaviorData[index] = data;
    this.markDirty();
    this.scheduleSync(this.quietPeriodMs);
  }

  getTargetData(targetID: number): PatientTargetDataModel[] | null {
    return this.session?.TargetData?.filter((t) => t.PatientTargetID === targetID) ?? null;
  }

  getAllTargetDataForTarget(targetID: number): PatientTargetDataModel[] {
    return (this.getTargetData(targetID) ?? []).filter((t) => !t.DeletedDate);
  }

  addTargetData(targetData: PatientTargetDataModel): void {
    this.saveTargetData(targetData);
  }

  saveTargetData(targetData: PatientTargetDataModel): void {
    if (!this.session?.TargetData) {
      return;
    }

    targetData.PatientSessionID = this.session.ID;
    targetData.RecordedDate ??= new Date().toServerString();

    const targetDataIndex = this.session.TargetData.findIndex(
      (t) =>
        t.PatientTargetID === targetData.PatientTargetID &&
        ((!!t.ID && t.ID === targetData.ID) || t.RecordedDate === targetData.RecordedDate)
    );
    const previous = targetDataIndex !== -1 ? this.session.TargetData[targetDataIndex] : undefined;
    if (targetDataIndex !== -1) {
      this.session.TargetData[targetDataIndex] = targetData;
    } else {
      this.session.TargetData.push(targetData);
    }

    const currencyDelta = (targetData.IntValue ?? 0) - (previous?.IntValue ?? 0);
    const target = this.targets.find((t) => t.ID === targetData.PatientTargetID);
    if (target?.TargetType === TargetType.Currency && currencyDelta !== 0) {
      target.CurrentBalance = (target.CurrentBalance ?? target.StartingBalance ?? 0) + currencyDelta;
    }

    this.markDirty();
    this.scheduleSync(this.quietPeriodMs);
  }

  getTargetsRunningData(SessionID: number, targetID: number): { SessionID: number; TargetID: number; Data: any } | null {
    this.loadTargetsRunningData();
    return this.targetsRunningDataCache.find((t) => t.SessionID === SessionID && t.TargetID === targetID) || null;
  }

  setTargetsRunningData(SessionID: number, targetID: number, data: any): void {
    this.loadTargetsRunningData();
    const targetRunningData = this.targetsRunningDataCache.find(
      (t) => t.SessionID === SessionID && t.TargetID === targetID
    );
    const shouldNotifyTimerLifecycleChange =
      !targetRunningData || !!targetRunningData.Data?.isStarted !== !!data?.isStarted;

    if (targetRunningData) {
      targetRunningData.Data = data;
    } else {
      this.targetsRunningDataCache.push({ SessionID, TargetID: targetID, Data: data });
    }
    localStorage.setItem(this.runningDataKey, JSON.stringify(this.targetsRunningDataCache));

    if (shouldNotifyTimerLifecycleChange) {
      this.dataChanged.update((n) => n + 1);
    }
  }

  claimIntervalCompletion(sessionID: number, targetID: number, startTime: number): boolean {
    if (!sessionID || !targetID || !startTime) {
      return false;
    }

    const running = this.getTargetsRunningData(sessionID, targetID);
    if (
      !running?.Data?.isStarted ||
      running.Data.startTime !== startTime ||
      running.Data.lastCompletedStartTime === startTime
    ) {
      return false;
    }

    this.setTargetsRunningData(sessionID, targetID, {
      ...running.Data,
      isStarted: false,
      lastCompletedStartTime: startTime,
    });
    return true;
  }

  completeInterval(sessionID: number, targetID: number, startTime: number): boolean {
    this.loadSessions();
    const session = this.sessions.find(x => x.ID === sessionID);
    const data = this.getTargetsRunningData(sessionID, targetID)?.Data;
    if (!session || !data || !this.claimIntervalCompletion(sessionID, targetID, startTime)) return false;

    const intervalData = {
      PatientSessionID: sessionID,
      IntValue: data.happened ? 1 : 0,
      TextValue: data.happened ? '1' : '0',
      RecordedDate: new Date().toServerString(),
    };
    // Older saved intervals have no type flag; behavior controls use this same offset.
    if (data.isIntervalBehavior || targetID >= 900000000) {
      session.BehaviorData ??= [];
      session.BehaviorData.push({...intervalData, PatientBehaviorID: targetID - 900000000});
    } else {
      session.TargetData ??= [];
      session.TargetData.push({...intervalData, PatientTargetID: targetID});
    }
    session.isDirty = true;
    this.changeVersion++;
    this.storeSession(session);
    this.dataChanged.update(n => n + 1);
    this.scheduleSync(this.quietPeriodMs);
    return true;
  }

  private markDirty(): void {
    if (!this.session) {
      return;
    }

    this.session.isDirty = true;
    this.changeVersion++;
    this.storeSession(this.session);
    this.dataChanged.update((n) => n + 1);
  }

  private scheduleSync(delayMs: number): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      this.syncPendingSessions().subscribe();
    }, delayMs);
  }

  private syncSession(session: StoredParentSession, showErrors: boolean): Observable<boolean> {
    if (this.syncingSessionIDs.has(session.ID)) {
      return of(false);
    }

    this.syncingSessionIDs.add(session.ID);
    const sentVersion = this.changeVersion;
    const syncingID = session.ID;
    const runningDataKey = this.runningDataKey;
    const storageKey = this.storageKey;
    let refused = false;
    const registerSession = (session.ID ?? 0) < 0
      ? this.parentApiService.StartSession({ patientID: session.PatientID!, startedDate: session.StartedDate ?? undefined }).pipe(map(serverSession => {
          const oldID = session.ID;
          const storedSessions: StoredParentSession[] = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
          localStorage.setItem(storageKey, JSON.stringify(storedSessions.filter(x => x.ID !== oldID)));
          if (storageKey === this.storageKey) this.sessions = this.sessions.filter(x => x.ID !== oldID);
          session.ID = serverSession.ID;
          this.syncingSessionIDs.add(session.ID);
          for (const data of [...(session.TargetData ?? []), ...(session.BehaviorData ?? [])]) data.PatientSessionID = session.ID;
          const runningData = JSON.parse(localStorage.getItem(runningDataKey) ?? '[]') as typeof this.targetsRunningDataCache;
          runningData.forEach(x => { if (x.SessionID === oldID) x.SessionID = session.ID!; });
          localStorage.setItem(runningDataKey, JSON.stringify(runningData));
          this.storeSession(session, storageKey);
          if (storageKey === this.storageKey) this.dataChanged.update(n => n + 1);
          return session;
        })) : of(session);
    return registerSession.pipe(switchMap(() => {
      if (storageKey !== this.storageKey) return throwError(() => new HttpErrorResponse({status: 401}));
      const { isDirty, ...body } = session;
      return this.parentApiService.SaveSession({ body });
    }),
      map((savedSession) => {
        // Only copy the new IDs: the boxes hold references to these trial objects.
        for (const targetData of session.TargetData ?? []) {
          const savedTargetData = savedSession.TargetData?.find(
            (t) =>
              t.PatientTargetID === targetData.PatientTargetID &&
              t.RecordedDate?.slice(0, 19) === targetData.RecordedDate?.slice(0, 19)
          );
          if (savedTargetData) {
            targetData.ID = savedTargetData.ID;
          }
        }
        for (const data of session.BehaviorData ?? []) {
          const saved = savedSession.BehaviorData?.find(x => x.PatientBehaviorID === data.PatientBehaviorID &&
            x.RecordedDate?.slice(0, 19) === data.RecordedDate?.slice(0, 19));
          if (saved) data.ID = saved.ID;
        }
        session.FinishedHandledDate = savedSession.FinishedHandledDate;
        if (sentVersion === this.changeVersion) {
          session.isDirty = false;
        }
        this.storeSession(session, storageKey);
        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error syncing parent session', session.ID, error);
        // Refusals (e.g. Take data in Spark was unticked) will not succeed on retry; connection and server errors will.
        if (error.status > 0 && error.status < 500) {
          refused = true;
          if (showErrors) {
            this.alertService.error(error.error?.Message || error.error?.message || 'This session could not be saved.');
          }
        } else {
          if (storageKey === this.storageKey) this.scheduleSync(this.retryDelayMs);
        }
        return of(false);
      }),
      finalize(() => {
        this.syncingSessionIDs.delete(syncingID);
        this.syncingSessionIDs.delete(session.ID);
        if (!refused && session === this.session && session.isDirty && !this.syncTimer && this.parentAuth.isSignedIn()) {
          this.scheduleSync(this.quietPeriodMs);
        }
      })
    );
  }

  private loadSessions(): void {
    if (this.loadedStorageKey === this.storageKey) return;
    this.leaveSession();
    this.loadedStorageKey = this.storageKey;
    this.sessions = JSON.parse(localStorage.getItem(this.storageKey) ?? '[]');
    this.targetsRunningDataCache = [];
  }

  private storeSession(session: StoredParentSession, storageKey = this.storageKey): void {
    if (storageKey !== this.storageKey) {
      const sessions: StoredParentSession[] = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
      const index = sessions.findIndex(x => x.ID === session.ID);
      if (index < 0) sessions.push(session); else sessions[index] = session;
      localStorage.setItem(storageKey, JSON.stringify(sessions));
      return;
    }
    const index = this.sessions.findIndex((s) => s.ID === session.ID);
    if (index !== -1) {
      this.sessions[index] = session;
    } else {
      this.sessions.push(session);
    }
    localStorage.setItem(this.storageKey, JSON.stringify(this.sessions));
  }

  private loadTargetsRunningData(): void {
    const targetsRunningData = localStorage.getItem(this.runningDataKey);
    if (targetsRunningData) {
      this.targetsRunningDataCache = JSON.parse(targetsRunningData);
    }
  }
}
